import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notification } from '@spark/ui-components/utils/notification';
import { atualizarEtapaProtocoloProgramacaoHavan, listarProgramacaoHavan, listarProgramacaoHavanDatas } from '../../services/programacaoHavanService';
import { formatDateOption } from '../../Listagem/hooks/useProgramacaoHavanListagem';

// Mesmo limite da programação: o turno vem inteiro. Vêm todas as cargas, para contar quantos protocolos o dia terá;
// a visão "só protocolos" filtra aqui.
const PAGE_SIZE = 300;
const CHAVES_TURNO = { primeiro: 1, segundo: 2 };
export const VISAO_TODAS = 'todas';
export const VISAO_PROTOCOLOS = 'protocolos';
const CHAVE_VISAO = 'emissaoProtocolo.visao';

// Preferência de tela do operador (não é marcação): fica no navegador.
const lerVisao = () => {
  try {
    return window.localStorage.getItem(CHAVE_VISAO) === VISAO_PROTOCOLOS ? VISAO_PROTOCOLOS : VISAO_TODAS;
  } catch {
    return VISAO_TODAS;
  }
};

const gravarVisao = visao => {
  try {
    window.localStorage.setItem(CHAVE_VISAO, visao);
  } catch {
    // Navegador sem localStorage: a visão vale só nesta tela aberta.
  }
};

const turnoVazio = () => ({ registros: [], carregando: false, erro: null });

const mapearRegistro = (registro, chaveTurno) => ({
  ...registro,
  chaveTurno,
  ct: registro.numero_carga || '',
  // Frota do cavalo seguida das carretas, como na planilha de protocolos ("2406, 197, 771").
  frotas: [registro.numero_frota, registro.frota_carreta_1, registro.frota_carreta_2, registro.frota_carreta_3]
    .map(frota => String(frota || '').trim())
    .filter(Boolean)
});

const mensagemDeErro = (error, padrao) => error?.response?.data?.error || error?.response?.data?.message || error?.message || padrao;

export const useEmissaoProtocolo = () => {
  const [dateOptions, setDateOptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [turnoAtivo, setTurnoAtivo] = useState(null);
  const [turnos, setTurnos] = useState({ primeiro: turnoVazio(), segundo: turnoVazio() });
  // "id:etapa" em gravação: o checkbox fica travado até a resposta.
  const [salvando, setSalvando] = useState([]);
  const [visao, setVisao] = useState(lerVisao);
  const selectedDateRef = useRef(null);
  const requestIdsRef = useRef({ primeiro: 0, segundo: 0 });

  const selectedDateOption = useMemo(() => dateOptions.find(option => option.value === selectedDate) || { diaSemana: '' }, [dateOptions, selectedDate]);

  const carregarTurno = useCallback(async (chaveTurno, data) => {
    if (!data) return;
    const requestId = ++requestIdsRef.current[chaveTurno];
    setTurnos(atual => ({ ...atual, [chaveTurno]: { ...atual[chaveTurno], carregando: true, erro: null } }));
    try {
      const resposta = await listarProgramacaoHavan({ data_programacao: data, turno: CHAVES_TURNO[chaveTurno], pagina: 1, tamanho_pagina: PAGE_SIZE });
      if (data !== selectedDateRef.current || requestId !== requestIdsRef.current[chaveTurno]) return;
      setTurnos(atual => ({ ...atual, [chaveTurno]: { registros: (resposta.registros || []).map(registro => mapearRegistro(registro, chaveTurno)), carregando: false, erro: null } }));
    } catch (error) {
      if (data !== selectedDateRef.current || requestId !== requestIdsRef.current[chaveTurno]) return;
      setTurnos(atual => ({ ...atual, [chaveTurno]: { ...atual[chaveTurno], carregando: false, erro: mensagemDeErro(error, 'Não foi possível carregar os protocolos deste turno.') } }));
    }
  }, []);

  useEffect(() => {
    let ativo = true;
    listarProgramacaoHavanDatas()
      .then(resposta => {
        if (!ativo) return;
        const opcoes = (resposta.registros || []).map(formatDateOption).sort((first, second) => second.value.localeCompare(first.value));
        setDateOptions(opcoes);
        setSelectedDate(atual => atual || opcoes[0]?.value || null);
      })
      .catch(() => { if (ativo) notification.error('Não foi possível carregar as datas da programação Havan.'); });
    return () => { ativo = false; };
  }, []);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
    if (!selectedDate) return;
    carregarTurno('primeiro', selectedDate);
    carregarTurno('segundo', selectedDate);
  }, [carregarTurno, selectedDate]);

  const handleSelectDate = useCallback(data => {
    selectedDateRef.current = data;
    setSelectedDate(data);
    setTurnos({ primeiro: turnoVazio(), segundo: turnoVazio() });
  }, []);

  const handleNextDate = useCallback(() => {
    const indice = dateOptions.findIndex(option => option.value === selectedDate);
    handleSelectDate(dateOptions[indice >= 0 ? (indice + 1) % dateOptions.length : 0]?.value || null);
  }, [dateOptions, handleSelectDate, selectedDate]);

  const handlePreviousDate = useCallback(() => {
    const indice = dateOptions.findIndex(option => option.value === selectedDate);
    handleSelectDate(dateOptions[indice > 0 ? indice - 1 : dateOptions.length - 1]?.value || null);
  }, [dateOptions, handleSelectDate, selectedDate]);

  const handleTurnoFilter = useCallback(turno => setTurnoAtivo(atual => (atual === turno ? null : turno)), []);

  const handleVisao = useCallback(nova => {
    setVisao(nova);
    gravarVisao(nova);
  }, []);

  const handleRetryTurno = useCallback(chaveTurno => carregarTurno(chaveTurno, selectedDateRef.current), [carregarTurno]);

  const aplicarNaLinha = (chaveTurno, id, campos) => setTurnos(atual => ({
    ...atual,
    [chaveTurno]: { ...atual[chaveTurno], registros: atual[chaveTurno].registros.map(registro => (registro.id === id ? { ...registro, ...campos } : registro)) }
  }));

  // Marca na hora (a linha muda de cor) e grava; se a API recusar, volta como estava e recarrega o turno.
  const handleAlternarEtapa = useCallback(async (registro, etapa, marcado) => {
    const chaveTurno = registro.chaveTurno || 'primeiro';
    const campo = etapa === 'recebido' ? 'protocolo_recebido' : 'protocolo_entregue';
    const chaveSalvando = `${registro.id}:${etapa}`;
    const anterior = { [campo]: registro[campo], [`${campo}_em`]: registro[`${campo}_em`], [`${campo}_por`]: registro[`${campo}_por`] };
    const data = selectedDateRef.current;

    setSalvando(atual => [...atual, chaveSalvando]);
    aplicarNaLinha(chaveTurno, registro.id, { [campo]: marcado, [`${campo}_em`]: marcado ? new Date().toISOString() : null, [`${campo}_por`]: null });
    try {
      await atualizarEtapaProtocoloProgramacaoHavan(registro.id, etapa, marcado);
      // Recarrega para trazer quem marcou e quando (gravados pela API).
      if (data === selectedDateRef.current) carregarTurno(chaveTurno, data);
    } catch (error) {
      if (data === selectedDateRef.current) {
        aplicarNaLinha(chaveTurno, registro.id, anterior);
        carregarTurno(chaveTurno, data);
      }
      notification.error(mensagemDeErro(error, `Não foi possível gravar o protocolo ${registro.ct || ''}.`));
    } finally {
      setSalvando(atual => {
        const indice = atual.indexOf(chaveSalvando);
        return indice < 0 ? atual : [...atual.slice(0, indice), ...atual.slice(indice + 1)];
      });
    }
  }, [carregarTurno]);

  const totais = useMemo(() => {
    const todas = [...turnos.primeiro.registros, ...turnos.segundo.registros];
    return { cargas: todas.length, protocolos: todas.filter(registro => registro.protocolo).length };
  }, [turnos]);

  const turnosVisiveis = useMemo(() => (visao === VISAO_PROTOCOLOS
    ? {
      primeiro: { ...turnos.primeiro, registros: turnos.primeiro.registros.filter(registro => registro.protocolo) },
      segundo: { ...turnos.segundo, registros: turnos.segundo.registros.filter(registro => registro.protocolo) }
    }
    : turnos), [turnos, visao]);

  return {
    dateOptions,
    selectedDate,
    selectedDateOption,
    turnoAtivo,
    turnos: turnosVisiveis,
    totais,
    visao,
    salvando,
    onSelectDate: handleSelectDate,
    onPreviousDate: handlePreviousDate,
    onNextDate: handleNextDate,
    onTurnoFilter: handleTurnoFilter,
    onRetryTurno: handleRetryTurno,
    onVisao: handleVisao,
    onAlternarEtapa: handleAlternarEtapa
  };
};
